/**
 * PageLayout — the app shell for authenticated routes.
 *
 * Wraps children with the Sidebar (left) and Header (top). Used by
 * every authed route through the AuthedLayout helper in App.tsx.
 *
 * OPT-OUT PROPS (Phase 1 NAV-1):
 *   hideHeader  — skip rendering the top Header bar. Pages with their
 *                 own hero/header strip (Dashboard, Portfolio, Products)
 *                 use this so the visual doesn't double up.
 *   noPadding   — skip the main padding. Pages that manage their own
 *                 max-width container + horizontal padding (Products,
 *                 Dashboard hero strip) use this so the body can be
 *                 full-bleed.
 *
 * Both default to false — easy case stays easy. Pages that need a
 * default Header + default padding just don't pass anything.
 *
 * RESPONSIVE BEHAVIOR:
 *   Desktop: sidebar fixed left, collapsible (preference persisted)
 *   Mobile : sidebar slides over content, overlay click closes it
 *
 * BG SWAP (Phase 1 NAV-1):
 *   bg-gray-50 → bg-background (brand token)
 */
import React, { useState, useEffect, useRef } from "react";
import TrialBanner from "@/components/subscription/TrialBanner";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import AdminBottomNav from "./AdminBottomNav";
import useAuthStore, { isCashier } from "@/stores/authStore";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PageLayoutProps {
  children: React.ReactNode;
  /** Page title shown in the top Header bar. Required even when
   *  hideHeader=true so it can populate document.title or breadcrumbs
   *  in future iterations. */
  title: string;
  /** Opt out of the top Header bar. Used by pages with their own
   *  hero/header strip (Dashboard, Portfolio, Products). */
  hideHeader?: boolean;
  /** Opt out of the main padding. Used by pages that manage their
   *  own container + horizontal padding. */
  noPadding?: boolean;
}

export const PageLayout = ({
  children,
  title,
  hideHeader = false,
  noPadding = false,
}: PageLayoutProps) => {
  // Get saved preference from localStorage, default to true (collapsed)
  const savedCollapsed = localStorage.getItem("sidebarCollapsed");
  const initialCollapsed =
    savedCollapsed !== null ? savedCollapsed === "true" : true;

  // Phones start with the menu closed (the bottom bar is there instead).
  const isPhone = () => typeof window !== "undefined" && window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = useState(() => !isPhone());
  const [phone, setPhone] = useState(isPhone);
  useEffect(() => {
    const onResize = () => setPhone(isPhone());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const { pathname } = useLocation();
  const role = useEffectiveRole() || useAuthStore.getState().user?.role;
  const bottomBar = !!role && !isCashier(role);

  // Opening a page from the menu on a phone closes the menu.
  useEffect(() => {
    if (isPhone()) setSidebarOpen(false);
  }, [pathname]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialCollapsed);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleCollapse = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", String(newState));
  };

  // Handle click outside to close sidebar (mobile only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        sidebarOpen &&
        window.innerWidth < 768
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  // Calculate sidebar width based on state
  // On a phone the menu slides over the page at full width.
  // On a phone the container keeps its width and slides off-screen: with
  // width 0, "translate -100%" moved nothing and the menu (fixed width)
  // stayed drawn over the page, catching every tap.
  const getSidebarWidth = () => {
    if (phone) return 256;
    if (!sidebarOpen) return 0;
    return sidebarCollapsed ? 64 : 256;
  };

  const sidebarWidth = getSidebarWidth();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed left-0 top-0 ${phone ? "z-50" : "z-30"} h-screen transition-all duration-300 ${
          sidebarOpen ? "translate-x-0" : "pointer-events-none invisible -translate-x-full"
        }`}
        style={{ width: sidebarWidth }}
        aria-hidden={!sidebarOpen || undefined}
      >
        <Sidebar collapsed={phone ? false : sidebarCollapsed} onToggle={phone ? () => setSidebarOpen(false) : toggleCollapse} />
      </div>

      {/* Main content */}
      <div
        className="flex w-full flex-col transition-all duration-300 ease-in-out"
        style={{ marginLeft: phone ? 0 : sidebarWidth }}
      >
        {!hideHeader && (
          <Header title={title}>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="mr-2 md:hidden"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </Header>
        )}
        <TrialBanner />
        <main className={`flex-1 ${noPadding ? "" : "p-4 md:p-6"} ${bottomBar ? "pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-6" : ""}`}>
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {bottomBar && !sidebarOpen && <AdminBottomNav onMenu={() => setSidebarOpen(true)} />}

      {/* Mobile toggle button — shown when sidebar is hidden on mobile (no bottom bar) */}
      {!sidebarOpen && !bottomBar && (
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleSidebar}
          className="fixed bottom-4 left-4 z-40 rounded-full shadow-lg md:hidden"
          aria-label="Open sidebar"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};
